import { zodResolver } from "@hookform/resolvers/zod";
import { RefreshCw } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getApiErrorI18nKey, useAllSchemas, useGoogleDriveFolders, useTriggerIngestion } from "@noa/api-client";
import { GoogleDriveIcon } from "./google-drive-icon";
import { GoogleDriveSyncConfigForm } from "./google-drive-sync-config-form";
import {
  buildGoogleDriveSyncRequest,
  googleDriveSyncDefaultValues,
  googleDriveSyncSchema,
  type GoogleDriveSyncForm,
} from "~/utils/google-drive-sync-form";
import { Alert, Button, Card, message, Switch, Text, Title } from "~/components/ui";

type GoogleDriveSyncPanelProps = {
  onNavigateToHistory?: () => void;
};

export function GoogleDriveSyncPanel({ onNavigateToHistory }: GoogleDriveSyncPanelProps) {
  const { t } = useTranslation("admin");
  const [lastJobId, setLastJobId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { control, handleSubmit, watch } = useForm<GoogleDriveSyncForm>({
    resolver: zodResolver(googleDriveSyncSchema),
    defaultValues: googleDriveSyncDefaultValues,
  });
  const withVision = watch("withVision");

  const { schemas, isLoading: schemasLoading } = useAllSchemas();
  const {
    folders,
    isLoading: foldersLoading,
    isError: foldersError,
    error: foldersLoadError,
    refetch: refetchFolders,
  } = useGoogleDriveFolders();
  const notConfigured = !foldersLoading && !foldersError && folders.length === 0;
  // A sync with no folder list would target every folder of a configuration the
  // backend can't read; only allow it once the list has loaded.
  const syncDisabled = foldersLoading || foldersError || notConfigured;

  const triggerSync = useTriggerIngestion();

  const handleSync = (values: GoogleDriveSyncForm) => {
    triggerSync.mutate(buildGoogleDriveSyncRequest(values), {
      onSuccess: (data) => {
        setLastJobId(data.job_id);
        setError(null);
        void message.success(t("uploadAdmin.sync.googleDrive.started"));
      },
      onError: () => {
        setError(t("uploadAdmin.sync.googleDrive.error"));
      },
    });
  };

  const folderOptions = folders.map((f) => ({
    value: f.name,
    label: `${f.name} (${f.extensions.join(", ")})`,
  }));

  const handleInvalid = () => {
    void message.error(t("uploadAdmin.sync.invalidForm"));
  };

  const schemaOptions = schemas.map((s) => ({
    value: s.schema_id,
    label: `${s.schema_id} — ${s.description || s.doc_type}`,
  }));

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <GoogleDriveIcon size={30} />
        <div>
          <Title level={4} className="!mb-0">
            {t("uploadAdmin.sync.googleDrive.title")}
          </Title>
          <Text tone="muted">{t("uploadAdmin.sync.googleDrive.description")}</Text>
        </div>
      </div>

      {foldersError && (
        <Alert
          type="error"
          message={t("uploadAdmin.sync.googleDrive.foldersError")}
          description={
            <>
              {foldersLoadError && <div>{t(getApiErrorI18nKey(foldersLoadError))}</div>}
              <div>{t("uploadAdmin.sync.googleDrive.foldersErrorDescription")}</div>
            </>
          }
          action={
            <Button size="small" onClick={() => void refetchFolders()}>
              {t("common:retry")}
            </Button>
          }
          showIcon
          data-testid="google-drive-folders-error"
        />
      )}

      {notConfigured && (
        <Alert
          type="warning"
          message={t("uploadAdmin.sync.googleDrive.notConfigured")}
          description={t("uploadAdmin.sync.googleDrive.notConfiguredDescription")}
          showIcon
          data-testid="google-drive-not-configured"
        />
      )}

      {/* Sync Action Card */}
      <Card size="small" className="bg-noa-hover">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex-1">
            <Title level={5} className="!mb-1">
              {t("uploadAdmin.sync.googleDrive.syncTitle")}
            </Title>
            <Text tone="muted">{t("uploadAdmin.sync.googleDrive.syncDescription")}</Text>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2">
                <Text className="text-xs">{t("uploadAdmin.form.withVision")}</Text>
                <Controller
                  name="withVision"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      size="small"
                      checked={field.value}
                      onChange={field.onChange}
                      aria-label={t("uploadAdmin.form.withVision")}
                    />
                  )}
                />
              </div>
              {!withVision && (
                <Text className="text-xs text-orange-600 text-right max-w-48">
                  ⚠️ {t("uploadAdmin.sync.maximo.visionWarning")}
                </Text>
              )}
            </div>
            <Button
              variant="primary"
              size="large"
              icon={<RefreshCw size={14} className={triggerSync.isPending ? "animate-spin" : undefined} />}
              loading={triggerSync.isPending}
              disabled={syncDisabled}
              onClick={handleSubmit(handleSync, handleInvalid)}
              data-testid="google-drive-sync-button"
            >
              {t("uploadAdmin.sync.googleDrive.startSync")}
            </Button>
          </div>
        </div>
      </Card>

      <GoogleDriveSyncConfigForm
        control={control}
        folderOptions={folderOptions}
        foldersLoading={foldersLoading}
        schemaOptions={schemaOptions}
        schemasLoading={schemasLoading}
      />

      {/* Error message */}
      {error && (
        <Alert
          type="error"
          message={t("common:error")}
          description={error}
          showIcon
          closable
          onClose={() => setError(null)}
        />
      )}

      {/* Success message */}
      {lastJobId && (
        <Alert
          type="success"
          message={t("uploadAdmin.sync.taskStarted")}
          description={
            <div className="inline-flex flex-col gap-2">
              <Text>
                {t("uploadAdmin.sync.jobId")}: <code className="bg-noa-hover px-2 py-1 rounded">{lastJobId}</code>
              </Text>
              {onNavigateToHistory && (
                <Button variant="link" size="small" className="!p-0 !h-auto" onClick={onNavigateToHistory}>
                  {t("uploadAdmin.sync.viewInHistory")} →
                </Button>
              )}
            </div>
          }
          showIcon
          closable
          onClose={() => setLastJobId(null)}
        />
      )}

      {/* How it works */}
      <Card title={t("uploadAdmin.sync.googleDrive.howItWorks")} size="small">
        <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 list-decimal pl-5 text-noa-muted">
          <li>{t("uploadAdmin.sync.googleDrive.step1")}</li>
          <li>{t("uploadAdmin.sync.googleDrive.step2")}</li>
          <li>{t("uploadAdmin.sync.googleDrive.step3")}</li>
          <li>{t("uploadAdmin.sync.googleDrive.step4")}</li>
        </ol>
      </Card>

      <Text tone="muted" className="text-xs">
        {t("uploadAdmin.sync.googleDrive.trademark")}
      </Text>
    </div>
  );
}
